import { useMemo } from 'react';
import { dialog } from '../../store/dialogStore';
import { isOwnerOnlyCapability } from './constants';

// Checklist de capabilities agrupada por seção (arquitetura-v2.md §6.13).
// `catalog` vem de GET /roles/capabilities — aceita tanto `{ sections, capabilities }`
// com `capabilities` em objeto (chave -> meta) quanto em array (meta com `key`),
// já que o formato exato não está fechado no contrato resumido desta entrega.
export function normalizeCatalog(raw) {
  if (!raw) return { sections: [], items: [] };
  const sections = Array.isArray(raw.sections) ? raw.sections : [];
  let items = [];
  if (Array.isArray(raw.capabilities)) {
    items = raw.capabilities.map((c) => ({ key: c.key || c.id, ...c }));
  } else if (raw.capabilities && typeof raw.capabilities === 'object') {
    items = Object.entries(raw.capabilities).map(([key, meta]) => ({ key, ...meta }));
  }
  return { sections, items };
}

export default function CapabilitiesChecklist({ catalog, selected, allowedKeys, onChange }) {
  const { sections, items } = useMemo(() => normalizeCatalog(catalog), [catalog]);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const canGrant = (key) => !allowedKeys || allowedKeys.has(key);

  const itemsBySection = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = it.section || 'geral';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return map;
  }, [items]);

  const emit = (nextSet) => onChange(Array.from(nextSet));

  const toggleOne = async (item) => {
    // Exclusiva do Proprietário — nunca concedível a outro grupo (POST/PUT
    // /roles recusa com 403). Defesa em profundidade: o checkbox já vem
    // disabled, isto cobre qualquer disparo programático do onChange.
    if (isOwnerOnlyCapability(item.key)) return;
    const next = new Set(selectedSet);
    const isBoardCap = item.scope === 'board';
    if (next.has(item.key)) {
      // Desmarcando board.view com outras board.* marcadas: avisa que anula a seção.
      if (item.key === 'board.view') {
        const otherBoardCaps = items.filter((it) => it.scope === 'board' && it.key !== 'board.view' && next.has(it.key));
        if (otherBoardCaps.length > 0) {
          const ok = await dialog.confirm(
            'Remover "Ver o quadro"',
            'Sem esta capability, nenhuma outra de "Quadro" tem efeito. Todas as capabilities de quadro deste grupo serão desmarcadas.',
            { confirmLabel: 'Remover mesmo assim', danger: true }
          );
          if (!ok) return;
          items.forEach((it) => { if (it.scope === 'board') next.delete(it.key); });
          emit(next);
          return;
        }
      }
      next.delete(item.key);
    } else {
      next.add(item.key);
      // Marcar qualquer board.* marca board.view junto (pré-requisito estrutural).
      if (isBoardCap && item.key !== 'board.view' && canGrant('board.view')) {
        next.add('board.view');
      }
    }
    emit(next);
  };

  const toggleSection = (sectionKey, sectionItems) => {
    const grantable = sectionItems.filter((it) => canGrant(it.key) && !isOwnerOnlyCapability(it.key));
    const allChecked = grantable.length > 0 && grantable.every((it) => selectedSet.has(it.key));
    const next = new Set(selectedSet);
    if (allChecked) {
      grantable.forEach((it) => next.delete(it.key));
    } else {
      grantable.forEach((it) => next.add(it.key));
    }
    emit(next);
  };

  if (sections.length === 0) {
    return <p className="text-xs text-text-secondary">Catálogo de capabilities indisponível.</p>;
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const sectionItems = itemsBySection.get(section.key) || [];
        if (sectionItems.length === 0) return null;
        const grantable = sectionItems.filter((it) => canGrant(it.key) && !isOwnerOnlyCapability(it.key));
        const allChecked = grantable.length > 0 && grantable.every((it) => selectedSet.has(it.key));
        return (
          <div key={section.key} className="border border-border/50 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(section.key, sectionItems)}
              disabled={grantable.length === 0}
              className="w-full flex items-center gap-2 px-3 py-2 bg-white/[0.02] text-xs font-semibold text-text-primary hover:bg-white/[0.04] transition-colors disabled:cursor-not-allowed"
            >
              <input type="checkbox" readOnly checked={allChecked} className="w-3.5 h-3.5 accent-accent pointer-events-none" />
              {section.label}
            </button>
            <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
              {sectionItems.map((item) => {
                const ownerOnly = isOwnerOnlyCapability(item.key);
                const disabled = ownerOnly || !canGrant(item.key);
                const title = ownerOnly
                  ? 'Exclusiva do Proprietário'
                  : disabled
                    ? 'Seu grupo não tem esta capability — você não pode concedê-la a outro grupo'
                    : item.label;
                return (
                  <label
                    key={item.key}
                    title={title}
                    className={`flex items-center gap-2 px-1.5 py-1 rounded text-xs ${disabled ? 'opacity-40 cursor-not-allowed' : 'text-text-primary hover:bg-white/[0.03] cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      checked={!ownerOnly && selectedSet.has(item.key)}
                      disabled={disabled}
                      onChange={() => toggleOne(item)}
                      className="w-3.5 h-3.5 accent-accent shrink-0"
                    />
                    <span className="truncate">{item.label || item.key}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
